import isEmpty from "lodash-es/isEmpty"
import map from "lodash-es/map"

import { coachPaginatedSessionAdapter, getSessionModalityEnumFromType, sessionBaseAdapter } from "@/3514/adapters"
import type {
    ICoachPaginatedSessionDto,
    ICoachPaginatedSessionModel,
    ISessionBaseDto,
    ISessionBaseModel
} from "@/3514/types"

import {
    ECoachDashboardParticipantsQueryModuleStatus,
    type ICoachDashboardParticipantsQueryDto,
    type ICoachDashboardParticipantsQueryModel,
    type ICoachDashboardParticipantsQueryParticipantDto,
    type ICoachDashboardParticipantsQueryParticipantModel,
    type ICoachDashboardSessionsQueryDto,
    type ICoachDashboardSessionsQueryModel,
    type ICoachSessionWithoutAttendanceMarksDto,
    type ICoachSessionWithoutAttendanceMarksModel,
    type TCoachDashboardParticipantsQueryModuleStatusDto
} from "./coach.dashboard.api.types"

function attendances(dtos: ICoachSessionWithoutAttendanceMarksDto[]): ICoachSessionWithoutAttendanceMarksModel[] {
    return isEmpty(dtos)
        ? []
        : map(
              dtos,
              ({
                  session_id,
                  session_time,
                  session_type,
                  session_modality,
                  title,
                  duration,
                  photo,
                  is_extra_party: isExtraParty
              }: ICoachSessionWithoutAttendanceMarksDto): ICoachSessionWithoutAttendanceMarksModel => ({
                  sessionId: session_id,
                  sessionTime: session_time,
                  sessionType: session_type,
                  sessionModality: getSessionModalityEnumFromType(session_modality),
                  title,
                  duration,
                  photo,
                  isExtraParty
              })
          )
}

function sessions(dto: ICoachDashboardSessionsQueryDto): ICoachDashboardSessionsQueryModel {
    return isEmpty(dto)
        ? null
        : {
              count: dto.count,
              filters: dto.filters,
              next: dto.next,
              previous: dto.previous,
              results: isEmpty(dto.results)
                  ? []
                  : map<ICoachPaginatedSessionDto, ICoachPaginatedSessionModel>(
                        dto.results,
                        coachPaginatedSessionAdapter
                    )
          }
}

function _getParticipantModuleStatusEnumFromDto(
    type: TCoachDashboardParticipantsQueryModuleStatusDto
): ECoachDashboardParticipantsQueryModuleStatus {
    switch (type) {
        case "queued":
            return ECoachDashboardParticipantsQueryModuleStatus.Queued
        case null:
        default:
            return null
    }
}

function participants(dto: ICoachDashboardParticipantsQueryDto): ICoachDashboardParticipantsQueryModel {
    return isEmpty(dto)
        ? null
        : {
              count: dto.count,
              next: dto.next,
              previous: dto.previous,
              results: isEmpty(dto.results)
                  ? []
                  : map<
                        ICoachDashboardParticipantsQueryParticipantDto,
                        ICoachDashboardParticipantsQueryParticipantModel
                    >(
                        dto.results,
                        (
                            participant: ICoachDashboardParticipantsQueryParticipantDto
                        ): ICoachDashboardParticipantsQueryParticipantModel => ({
                            cohortId: "id" in participant.cohorts ? participant.cohorts.id : null,
                            cohortName: "name" in participant.cohorts ? participant.cohorts.name : null,
                            cohortSlug: "slug" in participant.cohorts ? participant.cohorts.slug : null,
                            moduleStatus:
                                "status" in participant.current_module
                                    ? _getParticipantModuleStatusEnumFromDto(participant.current_module.status)
                                    : null,
                            moduleVersionId:
                                "version_id" in participant.current_module
                                    ? participant.current_module.version_id
                                    : null,
                            moduleVersionName:
                                "version_name" in participant.current_module
                                    ? participant.current_module.version_name
                                    : null,
                            firstName: participant.first_name,
                            id: participant.id,
                            lastName: participant.last_name,
                            photo: participant.photo,
                            upcomingSessions: isEmpty(participant.upcoming_sessions)
                                ? []
                                : map<ISessionBaseDto, ISessionBaseModel>(
                                      participant.upcoming_sessions,
                                      sessionBaseAdapter
                                  )
                        })
                    )
          }
}

export default { attendances, sessions, participants }
